import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router';
import { useCapabilityDetail } from '@/hooks/use-entity-detail';
import { CapabilityDetailLayout, WORKING_COPY_ID } from '@/components/capability-detail/CapabilityDetailLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    CheckCircle2,
    AlertTriangle,
    XCircle,
    Clock,
    ExternalLink,
    Key,
    Loader2,
    Puzzle,
    RefreshCw,
    ShieldOff,
    User,
} from 'lucide-react';
import { toast } from 'sonner';
import type { CapabilityRevision } from '@/types';
import { capabilityDetailPath } from '@/types';

interface ConnectionTypeConfig {
    integration_ref?: string;
    auth_type?: string;
    status?: string;
    owner?: {
        user_id?: string;
        tenant_id?: string;
        [key: string]: unknown;
    };
    credentials?: Record<string, unknown>;
    metadata?: Record<string, string>;
    token_state?: {
        expires_at?: string;
        refresh_token_present?: boolean;
        scopes?: string[];
        [key: string]: unknown;
    };
}

function extractTypeConfig(data: Record<string, unknown> | undefined, selectedRevisionId: string): ConnectionTypeConfig {
    if (!data) return {};
    if (selectedRevisionId !== WORKING_COPY_ID) {
        const immRevs = (data.immutable_revisions ?? []) as CapabilityRevision[];
        const rev = immRevs.find((r) => r.id === selectedRevisionId);
        if (rev?.type_config) return rev.type_config as ConnectionTypeConfig;
    }
    const wr = data.working_revision as { type_config?: Record<string, unknown> } | undefined;
    return (wr?.type_config ?? data.config ?? data.ext ?? {}) as ConnectionTypeConfig;
}

function StatusBadge({ status }: { status?: string }) {
    switch (status) {
        case 'active':
            return (
                <Badge className="bg-green-100 text-green-700 hover:bg-green-100 gap-1">
                    <CheckCircle2 size={12} /> Active
                </Badge>
            );
        case 'expired':
            return (
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 gap-1">
                    <AlertTriangle size={12} /> Expired
                </Badge>
            );
        case 'error':
            return (
                <Badge className="bg-red-100 text-red-700 hover:bg-red-100 gap-1">
                    <XCircle size={12} /> Error
                </Badge>
            );
        case 'revoked':
            return (
                <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 gap-1">
                    <XCircle size={12} /> Revoked
                </Badge>
            );
        case 'pending':
            return (
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 gap-1">
                    <Clock size={12} /> Pending
                </Badge>
            );
        default:
            return (
                <Badge variant="secondary" className="gap-1">
                    {status ?? 'Unknown'}
                </Badge>
            );
    }
}

function formatDate(dateStr?: string): string {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return dateStr;
    }
}

export default function ConnectionDetailPage() {
    const { appKey, name } = useParams<{ appKey: string; name: string }>();
    const { data, isLoading } = useCapabilityDetail(appKey!, name!);
    const [selectedRevisionId, setSelectedRevisionId] = useState(WORKING_COPY_ID);

    const tc = useMemo(
        () => extractTypeConfig(data as Record<string, unknown> | undefined, selectedRevisionId),
        [data, selectedRevisionId],
    );

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
        );
    }

    const connStatus = tc.status ?? (data as Record<string, unknown> | undefined)?.status as string | undefined;

    const infoSection = (
        <div className="mt-6 flex flex-col gap-4 rounded-xl border border-muted bg-card p-6 text-card-foreground shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900">Connection info</h2>
            <div className="flex flex-wrap gap-2">
                {tc.integration_ref && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700">
                        <Puzzle size={12} />
                        {tc.integration_ref}
                    </span>
                )}
                {tc.auth_type && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                        <Key size={12} />
                        {tc.auth_type.replace(/_/g, ' ')}
                    </span>
                )}
                <StatusBadge status={connStatus} />
            </div>
        </div>
    );

    return (
        <CapabilityDetailLayout
            typeLabel="Connection"
            selectedRevisionId={selectedRevisionId}
            onRevisionChange={setSelectedRevisionId}
            overviewChildren={infoSection}
        >
            <div className="space-y-6 pt-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Integration Reference */}
                    <Card className="border-muted">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-base">
                                <Puzzle size={16} className="text-violet-500" />
                                Integration
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {tc.integration_ref ? (
                                <>
                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-1">
                                            Integration Reference
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <code className="rounded bg-slate-100 px-2 py-0.5 text-sm font-mono text-slate-700">
                                                {tc.integration_ref}
                                            </code>
                                            {appKey && (
                                                <Link
                                                    to={capabilityDetailPath(appKey, 'integration', tc.integration_ref)}
                                                    className="text-slate-400 hover:text-slate-600 transition-colors"
                                                    title="View integration"
                                                >
                                                    <ExternalLink size={14} />
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                    {tc.auth_type && (
                                        <div>
                                            <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-1">
                                                Auth Type
                                            </p>
                                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                                                <Key size={10} className="mr-1" />
                                                {tc.auth_type.replace(/_/g, ' ')}
                                            </Badge>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <p className="text-sm text-slate-500 italic">
                                    No integration reference configured.
                                </p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Owner Info */}
                    <Card className="border-muted">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-base">
                                <User size={16} className="text-slate-500" />
                                Owner
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {tc.owner ? (
                                <dl className="grid grid-cols-1 gap-y-3 text-sm">
                                    {tc.owner.user_id && (
                                        <div>
                                            <dt className="text-slate-500 text-xs">User</dt>
                                            <dd className="font-mono text-slate-900">{tc.owner.user_id}</dd>
                                        </div>
                                    )}
                                    {tc.owner.tenant_id && (
                                        <div>
                                            <dt className="text-slate-500 text-xs">Tenant</dt>
                                            <dd className="font-mono text-slate-900">{tc.owner.tenant_id}</dd>
                                        </div>
                                    )}
                                    {Object.entries(tc.owner)
                                        .filter(([k]) => k !== 'user_id' && k !== 'tenant_id')
                                        .map(([k, v]) => (
                                            <div key={k}>
                                                <dt className="text-slate-500 text-xs capitalize">{k.replace(/_/g, ' ')}</dt>
                                                <dd className="font-mono text-slate-900">{String(v)}</dd>
                                            </div>
                                        ))}
                                </dl>
                            ) : (
                                <p className="text-sm text-slate-500 italic">No owner information available.</p>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Token / Session State */}
                {tc.token_state && (
                    <Card className="border-muted">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-base">
                                <Key size={16} className="text-slate-500" />
                                Token State
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                                {tc.token_state.expires_at && (
                                    <div>
                                        <dt className="text-slate-500 text-xs">Expires At</dt>
                                        <dd className="text-slate-900">{formatDate(tc.token_state.expires_at)}</dd>
                                    </div>
                                )}
                                {typeof tc.token_state.refresh_token_present === 'boolean' && (
                                    <div>
                                        <dt className="text-slate-500 text-xs">Refresh Token</dt>
                                        <dd>
                                            <Badge
                                                variant="secondary"
                                                className={tc.token_state.refresh_token_present
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-slate-100 text-slate-500'
                                                }
                                            >
                                                {tc.token_state.refresh_token_present ? 'Present' : 'Not available'}
                                            </Badge>
                                        </dd>
                                    </div>
                                )}
                                {tc.token_state.scopes && tc.token_state.scopes.length > 0 && (
                                    <div className="sm:col-span-2 lg:col-span-3">
                                        <dt className="text-slate-500 text-xs mb-1.5">Scopes</dt>
                                        <dd className="flex flex-wrap gap-1.5">
                                            {tc.token_state.scopes.map((scope) => (
                                                <Badge key={scope} variant="outline" className="text-[10px]">
                                                    {scope}
                                                </Badge>
                                            ))}
                                        </dd>
                                    </div>
                                )}
                            </dl>
                        </CardContent>
                    </Card>
                )}

                {/* Metadata */}
                {tc.metadata && Object.keys(tc.metadata).length > 0 && (
                    <Card className="border-muted">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base">Metadata</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                                {Object.entries(tc.metadata).map(([key, value]) => (
                                    <div key={key}>
                                        <dt className="text-slate-500 text-xs capitalize">
                                            {key.replace(/_/g, ' ')}
                                        </dt>
                                        <dd className="text-slate-900">{value}</dd>
                                    </div>
                                ))}
                            </dl>
                        </CardContent>
                    </Card>
                )}

                {/* Actions */}
                <Card className="border-muted">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base">Actions</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-3">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => toast.info('Token refresh initiated...')}
                                className="gap-1.5"
                            >
                                <RefreshCw size={14} />
                                Refresh Token
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => toast.warning('Revoking connection...')}
                                className="gap-1.5 text-destructive hover:text-destructive"
                            >
                                <ShieldOff size={14} />
                                Revoke Connection
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </CapabilityDetailLayout>
    );
}
